/**
 * Side Drawer Web Component
 * A reusable right-side drawer panel with backdrop.
 *
 * Usage:
 *   <side-drawer id="reviews-drawer" title="Reviews">
 *     <div slot="content">...your content...</div>
 *   </side-drawer>
 *
 * Open from anywhere:
 *   document.querySelector('#reviews-drawer').open();
 *
 * Or use the trigger element:
 *   <side-drawer-trigger drawer="#reviews-drawer">
 *     <button>Open drawer</button>
 *   </side-drawer-trigger>
 */
class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
  }

  connectedCallback() {
    this.drawerTitle = this.getAttribute('title') || '';
    this.render();
    this.setupEventListeners();
  }

  render() {
    const container = document.createElement('div');
    container.setAttribute('data-drawer-container', '');
    container.className = 'side-drawer-container fixed inset-0 z-[9999]';
    container.style.display = 'none';

    container.innerHTML = `
      <div data-backdrop class="fixed inset-0 bg-black/50 backdrop-blur-[5px] transition-opacity duration-150 opacity-0"></div>
      <div data-panel class="side-drawer-panel fixed top-0 right-0 bottom-0 bg-white translate-x-full transition-transform duration-300 ease-out flex flex-col">
        <div class="flex items-center justify-between pt-48 px-24 lg:px-48 pb-24 shrink-0">
          <h2 class="font-display font-bold text-24 tracking-[-0.72px] text-grey-600" data-title>${this.drawerTitle}</h2>
          <button type="button" data-close aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none"><g clip-path="url(#clip0_side_drawer)"><path d="M14 0C17.713 0 21.274 1.475 23.8995 4.1005C26.525 6.72601 28 10.287 28 14C28 17.713 26.525 21.274 23.8995 23.8995C21.274 26.525 17.713 28 14 28C10.287 28 6.72601 26.525 4.1005 23.8995C1.475 21.274 0 17.713 0 14C0 10.287 1.475 6.72601 4.1005 4.1005C6.72601 1.475 10.287 0 14 0ZM14 1C10.5522 1 7.24558 2.36964 4.80761 4.80761C2.36964 7.24558 1 10.5522 1 14C1 17.4478 2.36964 20.7544 4.80761 23.1924C7.24558 25.6304 10.5522 27 14 27C17.4478 27 20.7544 25.6304 23.1924 23.1924C25.6304 20.7544 27 17.4478 27 14C27 10.5522 25.6304 7.24558 23.1924 4.80761C20.7544 2.36964 17.4478 1 14 1ZM19.2 8.2L20.2 9.2L15.1 14.5L20.3 19.7L19.3 20.7L14 15.5L8.8 20.7L7.8 19.7L12.9 14.5L7.7 9.2L8.7 8.2L14 13.4L19.2 8.2Z" fill="#3B3B3B"/></g><defs><clipPath id="clip0_side_drawer"><rect width="28" height="28" fill="white"/></clipPath></defs></svg>
          </button>
        </div>
        <div data-content class="flex-1 overflow-y-auto px-24 lg:px-48 pb-64 pt-10">
        </div>
      </div>
    `;

    // Move slotted content into the drawer content area
    const contentSlot = this.querySelector('[slot="content"]');
    const contentArea = container.querySelector('[data-content]');
    if (contentSlot && contentArea) {
      contentArea.appendChild(contentSlot);
    }

    // Append to body so it sits above all stacking contexts (e.g. sticky header)
    document.body.appendChild(container);
    this._container = container;
  }

  setupEventListeners() {
    const backdrop = this._container.querySelector('[data-backdrop]');
    const closeBtn = this._container.querySelector('[data-close]');

    backdrop?.addEventListener('click', () => this.close());
    closeBtn?.addEventListener('click', () => this.close());

    this._handleEscape = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    };
    document.addEventListener('keydown', this._handleEscape);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._handleEscape);
    this._container?.remove();
  }

  open() {
    const container = this._container;
    const backdrop = container.querySelector('[data-backdrop]');
    const panel = container.querySelector('[data-panel]');

    if (!container) return;

    container.style.display = 'block';
    document.body.style.overflow = 'hidden';
    this.isOpen = true;

    // Trigger transitions on next frame
    requestAnimationFrame(() => {
      backdrop?.classList.remove('opacity-0');
      backdrop?.classList.add('opacity-100');
      panel?.classList.remove('translate-x-full');
      panel?.classList.add('translate-x-0');
    });

    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  close() {
    const container = this._container;
    const backdrop = container.querySelector('[data-backdrop]');
    const panel = container.querySelector('[data-panel]');

    if (!container) return;

    backdrop?.classList.remove('opacity-100');
    backdrop?.classList.add('opacity-0');
    panel?.classList.remove('translate-x-0');
    panel?.classList.add('translate-x-full');

    // Wait for transition to finish before hiding
    setTimeout(() => {
      container.style.display = 'none';
      document.body.style.overflow = '';
      this.isOpen = false;
    }, 300);

    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }

  setTitle(title) {
    const titleEl = this._container.querySelector('[data-title]');
    if (titleEl) titleEl.textContent = title;
  }
}

customElements.define('side-drawer', SideDrawer);

class SideDrawerTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', () => {
      const target = this.getAttribute('drawer');
      const drawer = document.querySelector(target);
      if (drawer) drawer.open();
    });
  }
}

customElements.define('side-drawer-trigger', SideDrawerTrigger);
